import React from 'react'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core'
import ArrowBackIos from '@material-ui/icons/ArrowBackIos'
import RefreshIcon from '@material-ui/icons/Refresh'
import Pagination from '@material-ui/lab/Pagination'
import { Redirect, useHistory } from 'react-router-dom'

import styles from '../settingsStyles'
import { useTitle } from '../../../navigation/helpers/useTitle'
import Loading from '../../dashboard/widgets/helpers/Loading'
import {
  fetchMyProfile,
  selectMyProfileStatus,
} from '../../dashboard/widgets/MyProfile/myProfileSlice'

const AUDIT_BATCH_SIZE = 20
const APPS_PER_PAGE = 50
const AUDIT_STATUS = {
  ALL: 'All',
  OPERATIONAL: 'Operational',
  UNAVAILABLE: 'Unavailable',
  VERIFYING: 'Verifying',
  NOT_CHECKED: 'Not Checked',
}
const STATUS_FILTERS = Object.values(AUDIT_STATUS)

function getStatusClass(classes, status) {
  return classes[`auditStatus${status.replace(/\s/g, '')}`]
}

async function checkAppUrl(app) {
  if (!app.url) {
    return AUDIT_STATUS.UNAVAILABLE
  }

  try {
    const response = await axios.head(app.url, {
      timeout: 10000,
      validateStatus: () => true,
    })

    return response.status >= 200 && response.status < 400
      ? AUDIT_STATUS.OPERATIONAL
      : AUDIT_STATUS.UNAVAILABLE
  } catch (error) {
    return AUDIT_STATUS.UNAVAILABLE
  }
}

function AuditTable({
  apps,
  classes,
  retryAppUrl,
  page,
  pageCount,
  setPage,
  setStatusFilter,
  statusFilter,
}) {
  return (
    <TableContainer>
      <Table className={classes.auditTable} aria-label="My Apps link audit">
        <TableHead>
          <TableRow>
            <TableCell className={classes.auditNameCell}>App Name</TableCell>
            <TableCell className={classes.auditUrlCell}>URL</TableCell>
            <TableCell align="center" className={classes.auditStatusCell}>
              <Box className={classes.auditStatusHeader}>
                <span>Status</span>
                <Select
                  className={classes.auditFilter}
                  inputProps={{ 'aria-label': 'Filter apps by status' }}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  value={statusFilter}
                >
                  {STATUS_FILTERS.map((filter) => (
                    <MenuItem key={filter} value={filter}>
                      {filter}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {apps.map((app) => (
            <TableRow key={app.id}>
              <TableCell className={classes.auditNameCell}>
                {app.title}
              </TableCell>
              <TableCell className={classes.auditUrlCell}>
                <a
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.auditUrl}
                >
                  {app.url}
                </a>
              </TableCell>
              <TableCell align="center" className={classes.auditStatusCell}>
                <Box className={classes.auditStatusAction}>
                  <Chip
                    className={`${classes.auditStatus} ${getStatusClass(
                      classes,
                      app.auditStatus,
                    )}`}
                    label={app.auditStatus}
                    size="small"
                  />
                  <Tooltip title={`Check ${app.title} status`}>
                    <span>
                      <IconButton
                        aria-label={`Check ${app.title} status`}
                        disabled={app.auditStatus === AUDIT_STATUS.VERIFYING}
                        onClick={() => retryAppUrl(app)}
                        size="small"
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pageCount > 1 ? (
        <Box className={classes.auditPagination}>
          <Pagination
            color="primary"
            count={pageCount}
            onChange={(event, nextPage) => setPage(nextPage)}
            page={page}
            showFirstButton
            showLastButton
          />
        </Box>
      ) : null}
    </TableContainer>
  )
}

function AppLinkAudit() {
  useTitle('My Apps Link Audit')
  const classes = styles()
  const history = useHistory()
  const dispatch = useDispatch()
  const { userProfile } = useSelector((state) => state.myProfile)
  const profileStatus = useSelector(selectMyProfileStatus)
  const [apps, setApps] = React.useState([])
  const [loadStatus, setLoadStatus] = React.useState('pending')
  const [statusFilter, setStatusFilter] = React.useState(AUDIT_STATUS.ALL)
  const [page, setPage] = React.useState(1)
  const [isCheckingAll, setIsCheckingAll] = React.useState(false)
  const isMounted = React.useRef(true)
  const filteredApps =
    statusFilter === AUDIT_STATUS.ALL
      ? apps
      : apps.filter((app) => app.auditStatus === statusFilter)
  const pageCount = Math.ceil(filteredApps.length / APPS_PER_PAGE)
  const currentPage = Math.min(page, pageCount || 1)
  const visibleApps = filteredApps.slice(
    (currentPage - 1) * APPS_PER_PAGE,
    currentPage * APPS_PER_PAGE,
  )

  React.useEffect(() => {
    if (page !== currentPage) {
      setPage(currentPage)
    }
  }, [currentPage, page])

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status)
    setPage(1)
  }

  const updateAppStatus = (id, auditStatus) => {
    setApps((currentApps) =>
      currentApps.map((app) => (app.id === id ? { ...app, auditStatus } : app)),
    )
  }

  const retryAppUrl = async (app) => {
    updateAppStatus(app.id, AUDIT_STATUS.VERIFYING)
    const auditStatus = await checkAppUrl(app)
    if (isMounted.current) {
      updateAppStatus(app.id, auditStatus)
    }
  }

  const checkAllAppUrls = async () => {
    if (isCheckingAll) {
      return
    }

    const appsToCheck = apps
    setIsCheckingAll(true)
    setApps((currentApps) =>
      currentApps.map((app) => ({
        ...app,
        auditStatus: AUDIT_STATUS.VERIFYING,
      })),
    )

    const auditBatch = async (start) => {
      if (start >= appsToCheck.length) {
        return
      }

      const batch = appsToCheck.slice(start, start + AUDIT_BATCH_SIZE)
      const results = await Promise.all(
        batch.map(async (app) => ({
          id: app.id,
          auditStatus: await checkAppUrl(app),
        })),
      )

      if (!isMounted.current) {
        return
      }

      const statuses = new Map(
        results.map(({ id, auditStatus }) => [id, auditStatus]),
      )
      setApps((currentApps) =>
        currentApps.map((app) =>
          statuses.has(app.id)
            ? { ...app, auditStatus: statuses.get(app.id) }
            : app,
        ),
      )

      await auditBatch(start + AUDIT_BATCH_SIZE)
    }

    try {
      await auditBatch(0)
    } finally {
      if (isMounted.current) {
        setIsCheckingAll(false)
      }
    }
  }

  React.useEffect(() => {
    if (profileStatus === 'idle') {
      dispatch(fetchMyProfile())
    }
  }, [dispatch, profileStatus])

  React.useEffect(
    () => () => {
      isMounted.current = false
    },
    [],
  )

  React.useEffect(() => {
    if (userProfile.hasAdminPermission !== 'true') {
      return
    }

    async function loadApps() {
      try {
        const response = await axios.get(
          process.env.REACT_APP_MYAPPS_APPS_ENDPOINT,
        )
        const auditApps = response.data.map((app) => ({
          ...app,
          auditStatus: AUDIT_STATUS.NOT_CHECKED,
        }))

        if (!isMounted.current) {
          return
        }

        setApps(auditApps)
        setLoadStatus('fulfilled')
      } catch (error) {
        if (isMounted.current) {
          setLoadStatus('error')
        }
      }
    }

    loadApps()
  }, [userProfile.hasAdminPermission])

  if (profileStatus === 'idle' || profileStatus === 'pending') {
    return <Loading />
  }

  if (userProfile.hasAdminPermission !== 'true') {
    return <Redirect to="/settings" />
  }

  return (
    <Box className={classes.centerPage}>
      <Grid
        container
        direction="column"
        alignItems="center"
        className={classes.auditContainer}
        wrap="nowrap"
      >
        <Grid
          item
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          className={classes.sectionTitle}
        >
          <IconButton
            onClick={() => history.replace('/settings')}
            aria-label="Back to Settings"
            className={classes.backButton}
          >
            <ArrowBackIos fontSize="small" />
          </IconButton>
          <Typography variant="h1">
            <b>My Apps Link Audit</b>
          </Typography>
        </Grid>
        <Grid item className={classes.fullWidth}>
          <Button
            color="primary"
            disabled={loadStatus !== 'fulfilled' || isCheckingAll}
            onClick={checkAllAppUrls}
            startIcon={<RefreshIcon />}
            variant="contained"
          >
            Check All Statuses
          </Button>
        </Grid>
        <Grid item className={classes.fullWidth}>
          <Card className={classes.linksCard}>
            {loadStatus === 'pending' ? <Loading /> : null}
            {loadStatus === 'error' ? (
              <Typography className={classes.auditMessage}>
                Unable to load the My Apps list. Please try again later.
              </Typography>
            ) : null}
            {loadStatus === 'fulfilled' ? (
              <AuditTable
                apps={visibleApps}
                classes={classes}
                page={currentPage}
                pageCount={pageCount}
                retryAppUrl={retryAppUrl}
                setPage={setPage}
                setStatusFilter={handleStatusFilterChange}
                statusFilter={statusFilter}
              />
            ) : null}
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default AppLinkAudit
